export enum ContentType {
  Parent = 'parent',
  Text = 'text',
  Table = 'table',
  TableTh = 'table_th',
  TableTd = 'table_td',
  List = 'list',
  Space = 'space',
  Answer = 'answer',
  //
  Image = 'image',
  Attachment = 'attachment',
  FillInBlank = 'fill_in_blank',
  // Essay = 'essay',
}

export enum SpaceType {
  Line = 'line',
  Empty = 'empty',
  Box = 'box',
}

export enum TableType {
  Border = 'border',
  Borderless = 'borderless',
}

export enum OrderType {
  Number = 'number',
  Roman = 'roman',
  Alphabet = 'alphabet',
  None = 'none',
}

export enum ImageSize {
  Full = 'full',
  Half = 'half',
  Third = 'third',
}

export interface ImageInterface {
  url: string;
  size: ImageSize;
}

export interface ContentInterface {
  type: ContentType;
  content:
    | TableThContent[]
    | TableTdContent[]
    | SubjectiveContent[]
    | ContentInterface[]
    | ImageInterface[]
    | string[]
    | string
    | null;
  config?: TableConfig | SpaceConfig | ParentConfig;
}

interface SpaceConfig {
  spaceType: SpaceType;
  spaceX: number;
  spaceY: number;
}

interface TableConfig {
  tableType: TableType;
}

interface ParentConfig {
  orderType: OrderType | null;
}

export class TextContent implements ContentInterface {
  readonly type = ContentType.Text;
  content = '';

  constructor(content: string) {
    this.content = content;
  }
}

export class ImageContent implements ContentInterface {
  readonly type = ContentType.Image;
  content: ImageInterface[] = [];

  constructor(content: ImageInterface[]) {
    this.content = content;
  }
}

export class ListContent implements ContentInterface {
  readonly type = ContentType.List;
  content: SubjectiveContent[] = [];

  constructor(content: SubjectiveContent[]) {
    this.content = content;
  }
}

export class TableContent implements ContentInterface {
  readonly type = ContentType.Table;
  config: TableConfig = {
    tableType: TableType.Border,
  };
  content: (TableThContent | TableTdContent)[] = [];

  constructor(
    content: (TableThContent | TableTdContent)[],
    tableType: TableType,
  ) {
    this.content = content;
    this.config.tableType = tableType;
  }
}

export class TableThContent implements ContentInterface {
  readonly type = ContentType.TableTh;
  content: SubjectiveContent[] = [];

  constructor(content: SubjectiveContent[]) {
    this.content = content;
  }
}

export class TableTdContent implements ContentInterface {
  readonly type = ContentType.TableTd;
  content: SubjectiveContent[] = [];

  constructor(content: SubjectiveContent[]) {
    this.content = content;
  }
}

export class AttachmentContent implements ContentInterface {
  readonly type = ContentType.Attachment;
  content: SubjectiveContent[] = [];

  constructor(content: SubjectiveContent[]) {
    this.content = content;
  }
}

export class SpaceContent implements ContentInterface {
  readonly type = ContentType.Space;
  config: SpaceConfig = {
    spaceType: SpaceType.Box,
    spaceX: 0,
    spaceY: 0,
  };
  content = null;

  constructor(spaceType: SpaceType, spaceX: number, spaceY: number) {
    this.config.spaceType = spaceType;
    this.config.spaceX = spaceX;
    this.config.spaceY = spaceY;
  }
}

export class FillInBlankContent implements ContentInterface {
  readonly type = ContentType.FillInBlank;
  config: ParentConfig = {
    orderType: OrderType.Alphabet,
  };
  content: string[] = [];

  constructor(content: string[], orderType?: OrderType) {
    // TODO: extract empty space for answer by delimiter (delimiter -> ##)
    this.content = content;
    if (orderType) this.config.orderType = orderType;
  }
}

export class AnswerContent implements ContentInterface {
  readonly type = ContentType.Answer;
  content: SubjectiveContent[] = [];
  answer: SubjectiveContent[] = [];

  constructor(content: SubjectiveContent[], answer: SubjectiveContent[]) {
    this.content = content;
    this.answer = answer;
  }
}

export class ParentContent implements ContentInterface {
  readonly type = ContentType.Parent;
  config: ParentConfig = {
    orderType: OrderType.None,
  };
  content: SubjectiveContent[] = [];

  constructor(
    content: SubjectiveContent[],
    orderType: OrderType | null = null,
  ) {
    this.content = content;
    this.config.orderType = orderType;
  }
}

export type SubjectiveContent =
  | TextContent
  | ImageContent
  | ListContent
  | TableContent
  | TableThContent
  | TableTdContent
  | AttachmentContent
  | SpaceContent
  | FillInBlankContent
  | AnswerContent
  | ParentContent;
